<template>
  <div class="skill-page">
    <!-- ==================== 头部 ==================== -->
    <div class="header">
      <h1>🔄 防抖与节流</h1>
      <p>
        防抖 <strong>(Debounce)</strong> 和 节流 <strong>(Throttle)</strong>
        是前端开发中两种常用的性能优化策略。
        它们都能限制函数的执行频率，但适用场景不同。以下通过两个 Vue2 案例直观对比。
      </p>
    </div>

    <!-- ==================== 概念说明 ==================== -->
    <div class="concepts">
      <div class="concept-card">
        <div class="tag tag-debounce">防抖 Debounce</div>
        <p>
          在事件被触发后，等待一段时间（如 <code>500ms</code>）再执行回调。
          如果在这段等待时间内事件再次被触发，则重新计时。<br /><br />
          <strong>🎯 适用场景：</strong>搜索框输入、窗口 resize、表单验证 —
          只需要用户「停止操作」后再执行。
        </p>
      </div>
      <div class="concept-card">
        <div class="tag tag-throttle">节流 Throttle</div>
        <p>
          在指定的时间间隔内（如 <code>500ms</code>）最多执行一次回调。
          无论事件触发多频繁，都严格按照固定频率执行。<br /><br />
          <strong>🎯 适用场景：</strong>按钮点击防重复提交、页面滚动、
          鼠标移动追踪 — 需要「固定频率」执行。
        </p>
      </div>
    </div>

    <!-- ========== 防抖案例 ========== -->
    <div class="app-container">
      <div class="case">
        <div class="case-header">
          <span>🔍 防抖案例 — 搜索框</span>
          <span class="badge badge-debounce">500ms</span>
        </div>
        <div class="case-body">
          <input
            class="search-box"
            type="text"
            v-model="debounce.query"
            @input="onDebounceInput"
            placeholder="输入关键词试试…"
          />
          <div class="search-result">
            <p>📝 实时输入：<strong>{{ debounce.query }}</strong></p>
            <p>⏳ 防抖搜索（500ms 后执行）：<strong>{{ debounce.result }}</strong></p>
            <p class="search-tip">
              快速连续输入时，每次输入都会重置计时器，
              只有你停下来超过 500ms，才会执行「搜索」。
            </p>
          </div>

          <div class="log-section">
            <h4>📋 「搜索」执行日志</h4>
            <div class="log-list" ref="debounceLog">
              <div
                class="log-item"
                v-for="(item, i) in debounce.logs"
                :key="i"
              >
                <span class="log-debounce">{{ item.text }}</span>
                <span class="log-time">{{ item.time }}</span>
              </div>
              <div v-if="debounce.logs.length === 0" class="log-empty">暂无记录</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== 节流案例 ========== -->
      <div class="case">
        <div class="case-header">
          <span>⚡ 节流案例 — 提交按钮</span>
          <span class="badge badge-throttle">1000ms / 1 次</span>
        </div>
        <div class="case-body">
          <div class="throttle-btn-area">
            <button class="action-btn" @click="onThrottleClick">
              🚀 立即提交
            </button>
            <div class="cooldown-bar">
              <div
                class="cooldown-bar-inner"
                :style="{ width: throttle.cooldownPct + '%' }"
              ></div>
            </div>
          </div>

          <div class="counter-row">
            <div class="counter-item">
              <span class="num num-raw">{{ throttle.clickCount }}</span>
              <span class="label">⚡ 点击次数</span>
            </div>
            <div class="counter-item">
              <span class="num num-throttled">{{ throttle.execCount }}</span>
              <span class="label">✅ 实际执行次数</span>
            </div>
          </div>

          <p class="throttle-tip">
            疯狂点击只有第一次生效，之后每隔 1000ms 才能再次「提交」。
          </p>

          <div class="log-section">
            <h4>📋 「提交」执行日志</h4>
            <div class="log-list" ref="throttleLog">
              <div
                class="log-item"
                v-for="(item, i) in throttle.logs"
                :key="i"
              >
                <span class="log-throttle">{{ item.text }}</span>
                <span class="log-time">{{ item.time }}</span>
              </div>
              <div v-if="throttle.logs.length === 0" class="log-empty">暂无记录</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========== 节流案例 2：鼠标移动 ========== -->
    <div class="app-container mouse-container">
      <div class="case">
        <div class="case-header">
          <span>🖱️ 节流案例 2 — 鼠标移动追踪</span>
          <span class="badge badge-throttle">200ms</span>
        </div>
        <div class="case-body">
          <div
            class="mouse-area"
            :class="{ active: mouse.active }"
            @mousemove="onMouseMove"
          >
            <div class="hint" v-if="!mouse.active">← 在此区域内移动鼠标 →</div>
            <div class="mouse-coords" v-if="mouse.active">
              <span class="raw">🟡 实际移动：{{ mouse.rawX }}, {{ mouse.rawY }}</span>
              <span class="throttled">🟢 节流输出（200ms）：{{ mouse.tX }}, {{ mouse.tY }}</span>
            </div>
          </div>
          <div class="mouse-tip">
            鼠标每移动一个像素都会触发，但只有每 200ms 才更新一次坐标显示。
          </div>
        </div>
      </div>

      <!-- 占位保持布局对称 -->
      <div class="case placeholder-case"></div>
    </div>

    <!-- ==================== 源码展示 ==================== -->
    <div class="code-section">
      <h3>📄 工具函数实现</h3>
      <div class="code-block">
        <span class="cm">// ----- 防抖 Debounce -----</span>
        <br />
        <span class="kw">function</span> <span class="fn">debounce</span>(fn, delay) {
        <br />
        &nbsp;&nbsp;<span class="kw">let</span> timer = <span class="kw">null</span>;
        <br />
        &nbsp;&nbsp;<span class="kw">return function</span>(...args) {
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;clearTimeout(timer);
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;timer = setTimeout(() =&gt; {
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;fn.apply(<span class="kw">this</span>, args);
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;}, delay);
        <br />
        &nbsp;&nbsp;};
        <br />
        }
        <br /><br />
        <span class="cm">// ----- 节流 Throttle（时间戳版）-----</span>
        <br />
        <span class="kw">function</span> <span class="fn">throttle</span>(fn, interval) {
        <br />
        &nbsp;&nbsp;<span class="kw">let</span> lastTime = <span class="num">0</span>;
        <br />
        &nbsp;&nbsp;<span class="kw">return function</span>(...args) {
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;<span class="kw">const</span> now = Date.now();
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;<span class="kw">if</span> (now - lastTime &gt;= interval) {
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;lastTime = now;
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;fn.apply(<span class="kw">this</span>, args);
        <br />
        &nbsp;&nbsp;&nbsp;&nbsp;}
        <br />
        &nbsp;&nbsp;};
        <br />
        }
      </div>
    </div>
  </div>
</template>

<script>
// ============================
// 工具函数
// ============================

/** 防抖 */
function debounce(fn, delay) {
  let timer = null
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

/** 节流（时间戳版，立即执行） */
function throttle(fn, interval) {
  let lastTime = 0
  return function (...args) {
    const now = Date.now()
    if (now - lastTime >= interval) {
      lastTime = now
      fn.apply(this, args)
    }
  }
}

/** 格式化时间 */
function nowStr() {
  const d = new Date()
  return d.toLocaleTimeString('zh-CN', { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0')
}

export default {
  name: 'DebounceThrottle',
  data() {
    return {
      // --- 防抖数据 ---
      debounce: {
        query: '',
        result: '（等待输入…）',
        logs: []
      },

      // --- 节流数据 ---
      throttle: {
        clickCount: 0,
        execCount: 0,
        logs: [],
        cooldownPct: 0,
        cooldownTimer: null
      },

      // --- 鼠标数据 ---
      mouse: {
        active: false,
        rawX: 0,
        rawY: 0,
        tX: 0,
        tY: 0
      }
    }
  },

  created() {
    // 用防抖包装「搜索执行」
    this.doSearch = debounce(function () {
      const q = this.debounce.query.trim()
      this.debounce.result = q ? '🔍 搜索：' + q : '（等待输入…）'
      this.debounce.logs.unshift({
        text: '🔍 搜索 "' + (q || '(空)') + '"',
        time: nowStr()
      })
      if (this.debounce.logs.length > 30) this.debounce.logs.length = 30
      this.$nextTick(() => {
        const el = this.$refs.debounceLog
        if (el) el.scrollTop = 0
      })
    }, 500)

    // 用节流包装「提交执行」
    this.doThrottleSubmit = throttle(function () {
      this.throttle.execCount++
      this.throttle.logs.unshift({
        text: '✅ 提交成功 #' + this.throttle.execCount,
        time: nowStr()
      })
      if (this.throttle.logs.length > 30) this.throttle.logs.length = 30
      this.$nextTick(() => {
        const el = this.$refs.throttleLog
        if (el) el.scrollTop = 0
      })
      // 冷却动画
      this.startCooldown()
    }, 1000)

    // 用节流包装鼠标坐标更新
    this.doThrottleMouse = throttle(function (x, y) {
      this.mouse.tX = x
      this.mouse.tY = y
    }, 200)
  },

  beforeDestroy() {
    // 清理冷却动画定时器
    if (this.throttle.cooldownTimer) {
      clearInterval(this.throttle.cooldownTimer)
      this.throttle.cooldownTimer = null
    }
  },

  methods: {
    // ---------- 防抖 ----------
    onDebounceInput() {
      this.doSearch()
    },

    // ---------- 节流：按钮 ----------
    onThrottleClick() {
      this.throttle.clickCount++
      this.doThrottleSubmit()
    },

    startCooldown() {
      if (this.throttle.cooldownTimer) {
        clearInterval(this.throttle.cooldownTimer)
      }
      this.throttle.cooldownPct = 100
      const start = Date.now()
      const duration = 1000
      this.throttle.cooldownTimer = setInterval(() => {
        const elapsed = Date.now() - start
        const remain = Math.max(0, 100 - (elapsed / duration) * 100)
        this.throttle.cooldownPct = remain
        if (remain <= 0) {
          clearInterval(this.throttle.cooldownTimer)
          this.throttle.cooldownTimer = null
        }
      }, 30)
    },

    // ---------- 节流：鼠标 ----------
    onMouseMove(e) {
      this.mouse.active = true
      this.mouse.rawX = e.clientX
      this.mouse.rawY = e.clientY
      this.doThrottleMouse(e.clientX, e.clientY)
    }
  }
}
</script>

<style scoped>
.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 32px 20px;
  text-align: center;
  border-radius: 12px;
  margin-bottom: 24px;
}

.header h1 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 2px;
  margin: 0;
}

.header p {
  margin-top: 10px;
  font-size: 14px;
  opacity: 0.85;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
}

.concepts {
  display: flex;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto 24px;
}

.concept-card {
  flex: 1;
  background: var(--surface-1);
  border-radius: 12px;
  padding: 24px;
  box-shadow: var(--shadow-surface-1);
}

.concept-card .tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  margin-bottom: 10px;
}

.tag-debounce { background: #e8f4fd; color: #1890ff; }
.tag-throttle { background: #f6ffed; color: #52c41a; }

.concept-card p {
  font-size: 14px;
  line-height: 1.8;
  color: #555;
  margin: 0;
}

.concept-card code {
  background: var(--surface-2);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.app-container {
  max-width: 1200px;
  margin: 0 auto 24px;
  display: flex;
  gap: 24px;
}

.case {
  flex: 1;
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  overflow: hidden;
}

.case-header {
  padding: 18px 24px;
  font-size: 18px;
  font-weight: 600;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  display: flex;
  align-items: center;
  gap: 10px;
}

.case-header .badge {
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 20px;
  font-weight: 600;
}

.badge-debounce { background: #e8f4fd; color: #1890ff; }
.badge-throttle { background: #f6ffed; color: #52c41a; }

.case-body {
  padding: 24px;
}

.search-box {
  width: 100%;
  padding: 12px 16px;
  font-size: 15px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  outline: none;
  transition: border .2s;
  box-sizing: border-box;
}

.search-box:focus {
  border-color: #667eea;
}

.search-result {
  margin-top: 16px;
  padding: 16px;
  background: var(--surface-1);
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.search-result p {
  font-size: 14px;
  margin-bottom: 4px;
}

.search-result strong {
  color: #667eea;
}

.search-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #aaa;
}

.throttle-btn-area {
  text-align: center;
  padding: 20px 0;
}

.action-btn {
  padding: 14px 48px;
  font-size: 18px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  cursor: pointer;
  transition: transform .15s, box-shadow .15s;
  box-shadow: 0 4px 14px rgba(102, 126, 234, .35);
  letter-spacing: 1px;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, .45);
}

.action-btn:active {
  transform: translateY(0);
}

.action-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
  transform: none;
}

.counter-row {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 20px;
}

.counter-item {
  text-align: center;
}

.counter-item .num {
  font-size: 36px;
  font-weight: 700;
  display: block;
}

.counter-item .label {
  font-size: 13px;
  color: #888;
}

.num-raw { color: #ff4d4f; }
.num-throttled { color: #52c41a; }

.throttle-tip {
  text-align: center;
  font-size: 13px;
  color: #aaa;
  margin-top: 4px;
}

.mouse-area {
  margin-top: 20px;
  padding: 30px;
  background: var(--surface-1);
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  text-align: center;
  user-select: none;
  transition: background .2s;
  cursor: crosshair;
}

.mouse-area.active {
  background: #f0f5ff;
  border-color: #667eea;
}

.mouse-area .hint {
  font-size: 14px;
  color: #999;
}

.mouse-coords {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 8px;
}

.mouse-coords span {
  font-size: 13px;
}

.mouse-coords .raw { color: #ff4d4f; }
.mouse-coords .throttled { color: #52c41a; }

.mouse-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #aaa;
  text-align: center;
}

.placeholder-case {
  visibility: hidden;
}

.log-section {
  margin-top: 20px;
}

.log-section h4 {
  font-size: 14px;
  color: #888;
  margin-bottom: 8px;
}

.log-list {
  max-height: 180px;
  overflow-y: auto;
  background: var(--surface-1);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'SFMono-Regular', Consolas, monospace;
}

.log-list::-webkit-scrollbar {
  width: 4px;
}

.log-list::-webkit-scrollbar-thumb {
  background: var(--surface-2);
  border-radius: 2px;
}

.log-item {
  padding: 4px 0;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  display: flex;
  justify-content: space-between;
}

.log-item:last-child {
  border-bottom: none;
}

.log-time {
  color: #bbb;
  font-size: 12px;
}

.log-debounce { color: #1890ff; }
.log-throttle { color: #52c41a; }

.log-empty {
  color: #ccc;
}

.cooldown-bar {
  margin-top: 12px;
  height: 6px;
  background: var(--surface-2);
  border-radius: 3px;
  overflow: hidden;
}

.cooldown-bar-inner {
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  border-radius: 3px;
  transition: width .1s linear;
}

.code-section {
  max-width: 1200px;
  margin: 0 auto 60px;
}

.code-section h3 {
  font-size: 20px;
  margin-bottom: 16px;
  color: #333;
}

.code-block {
  background: #1e1e2e;
  color: #cdd6f4;
  border-radius: 12px;
  padding: 24px;
  font-size: 14px;
  line-height: 1.8;
  overflow-x: auto;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace;
}

.code-block .kw { color: #cba6f7; }
.code-block .fn { color: #89b4fa; }
.code-block .str { color: #a6e3a1; }
.code-block .cm { color: #6c7086; }
.code-block .num { color: #fab387; }

@media (max-width: 800px) {
  .concepts,
  .app-container {
    flex-direction: column;
  }
}
</style>
